package com.hyperether.goodjob.repository

import androidx.compose.runtime.mutableStateOf
import com.hyperether.goodjob.database.Contact
import com.hyperether.goodjob.database.Site
import com.hyperether.goodjob.database.Team
import com.hyperether.goodjob.database.User
import com.hyperether.goodjob.database.Workspace
import com.hyperether.goodjob.models.AddressObject
import com.hyperether.goodjob.models.Assignees
import com.hyperether.goodjob.models.AssigneesRequest
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.Note
import com.hyperether.goodjob.models.Plan
import com.hyperether.goodjob.models.Skills
import com.hyperether.goodjob.models.Step
import com.hyperether.goodjob.models.UserTeam
import com.hyperether.goodjob.repository.remote.model.ContactRequest
import com.hyperether.goodjob.repository.remote.model.FileObject
import com.hyperether.goodjob.repository.remote.model.JobRequest
import com.hyperether.goodjob.repository.remote.model.UpdateWorkspaceRequest
import com.hyperether.goodjob.repository.remote.model.UserRequest
import com.hyperether.goodjob.repository.remote.model.WorkingHours
import com.hyperether.goodjob.scenes.addNew.StepData
import com.hyperether.goodjob.util.Constants
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.serializer
import kotlinx.serialization.encodeToString

class Mapper {

    fun toWorkspace(workspace: Workspace): com.hyperether.goodjob.models.Workspace {
        return com.hyperether.goodjob.models.Workspace(
            _id = workspace._id,
            id = workspace.id,
            name = workspace.name,
            planId = workspace.plan,
            type = workspace.type,
            userId = workspace.user_id,
            lastAccessed = workspace.lastAccessed
        )
    }

    fun toUpdateWorkspaceRequest(workspace: com.hyperether.goodjob.models.Workspace): UpdateWorkspaceRequest {
        return UpdateWorkspaceRequest(
            name = workspace.name,
            planId = workspace.planId,
            type = workspace.type,
            userId = workspace.userId,
            lastAccessed = workspace.lastAccessed
        )
    }

    fun toJob(job: com.hyperether.goodjob.database.Job): Job {
        return Job(
            id = job._id,
            jobTitle = job.jobTitle,
            status = job.status,
            assignees = try {
                job.assignees?.let { Mapper().jsonAsObject<Assignees>(it) }
            } catch (e: Exception) {
                null
            },
            creatorId = job.creatorId,
            workspaceId = job.workspaceId,
            start_at = job.start_at,
            end_at = job.end_at,
            repeat = job.repeat,
            repeatInterval = try {
                job.repeatInterval?.let { Mapper().jsonAsList<String>(it) }
            } catch (e: Exception) {
                null
            },
            dueDate = job.dueDate,
            startDate = job.startDate,
            jobDuration = job.jobDuration,
            steps = try {
                job.steps?.let { Mapper().jsonAsList<Step>(it) } ?: emptyList()
            } catch (e: Exception) {
                null
            },
            userId = job.userId,
            contactSiteId = job.contactId,
            contactSiteName = job.contactSiteName,
            contactParentId = job.contactParentId,
            noteObjects = try {
                job.notes?.let { Mapper().jsonToListNotes(it) }
            } catch (e: Exception) {
                null
            },
            fileObjects = try {
                job.fileObjects?.let { Mapper().jsonAsList<FileObject>(it) }
            } catch (e: Exception) {
                null
            },
            completionReport = job.completionReport
        )
    }

    fun toSite(site: Site): com.hyperether.goodjob.models.Site {
        return com.hyperether.goodjob.models.Site(
            id = site._id,
            personName = site.personName,
            addressObject = Mapper().jsonAsObject(site.personAddress),
            personPhone = site.personPhone,
            personEmail = site.personEmail,
            siteName = site.siteName,
            parentId = site.parentId,
            parentName = site.parentName,
            workspaceId = site.workspaceId
        )
    }

    fun toJobUpdateRequest(job: com.hyperether.goodjob.database.Job): JobRequest {
        val assignees = job.assignees?.let { Mapper().jsonAsObject<Assignees>(it) }
        val assigneesRequest = AssigneesRequest(
            employees = assignees?.employees?.map { it?._id }?.distinct(),
            teams = assignees?.teams?.map { it?._id }?.distinct(),
        )
        return JobRequest(
            jobTitle = job.jobTitle,
            status = job.status,
            assignees = assigneesRequest,
            creatorId = job.creatorId,
            workspaceId = job.workspaceId,
            start_at = job.start_at,
            end_at = job.end_at,
            repeat = job.repeat,
            repeatInterval = job.repeatInterval?.let { Mapper().jsonAsList<String>(it) },
            dueDate = job.dueDate,
            startDate = job.startDate,
            jobDuration = job.jobDuration,
            steps = job.steps?.let { Mapper().jsonAsList<Step>(it) } ?: emptyList(),
            userId = job.userId,
            contactSiteId = job.contactId,
            contactSiteName = job.contactSiteName,
            contactParentId = job.contactParentId,
            fileObjects = job.fileObjects?.let { Mapper().jsonAsList(it) },
            noteObjects = job.notes?.let { Mapper().jsonToListNotes(it) }
        )
    }

    fun toJobUpdateRequest(job: Job): JobRequest {
        val assignees = job.assignees
        val assigneesRequest = AssigneesRequest(
            employees = assignees?.employees?.map { it?._id }?.distinct(),
            teams = assignees?.teams?.map { it?._id }?.distinct(),
        )
        return JobRequest(
            jobTitle = job.jobTitle,
            status = job.status,
            assignees = assigneesRequest,
            creatorId = job.creatorId,
            workspaceId = job.workspaceId,
            start_at = job.start_at,
            end_at = job.end_at,
            repeat = job.repeat,
            repeatInterval = job.repeatInterval,
            dueDate = job.dueDate,
            startDate = job.startDate,
            jobDuration = job.jobDuration,
            steps = job.steps.orEmpty(),
            userId = job.userId,
            contactSiteId = job.contactSiteId,
            contactSiteName = job.contactSiteName,
            contactParentId = job.contactParentId,
            noteObjects = job.noteObjects,
            fileObjects = job.fileObjects
        )
    }

    fun toUser(dbUser: User): com.hyperether.goodjob.models.User {
        val jsonParser = Json { ignoreUnknownKeys = true }

        return com.hyperether.goodjob.models.User(
            _id = dbUser._id,
            fullName = dbUser.fullName,
            email = dbUser.email,
            password = dbUser.password,
            confirmStatus = dbUser.confirmStatus,
            role = dbUser.role,
            hash = null,
            type = null,
            workspaceId = dbUser.workspace_ids,
            planId = dbUser.planId,
            phone = dbUser.phone,
            zip = dbUser.zip,
            country = dbUser.contry,
            city = dbUser.city,
            address = dbUser.address,
            status = dbUser.status,
            skills = dbUser.skills?.let {
                try {
                    jsonParser.decodeFromString<List<String>>(it)
                } catch (e: Exception) {
                    null
                }
            },
            teams = dbUser.teams?.let {
                try {
                    jsonParser.decodeFromString<List<UserTeam>>(it)
                } catch (e: Exception) {
                    null
                }
            },
            upcomingLeave = dbUser.upcomingLeave,
            workingHours = dbUser.workingHours?.let {
                try {
                    jsonParser.decodeFromString<WorkingHours>(it)
                } catch (e: Exception) {
                    null
                }
            },
            pendingPlanId = dbUser.pendingPlanId,
            subscriptionEndsAt = dbUser.subscriptionEndsAt?.let {
                try {
                    it.toLong()
                } catch (e: Exception) {
                    null
                }
            },
            lang = dbUser.lang
        )
    }

    fun toTeam(teamDb: Team): com.hyperether.goodjob.models.Team {
        return com.hyperether.goodjob.models.Team(
            _id = teamDb._id,
            name = teamDb.name,
            workspaceId = teamDb.workspaceId,
            employees = teamDb.employees?.let {
                Mapper().jsonAsList<com.hyperether.goodjob.models.User>(
                    it
                )
            } ?: emptyList(),
            type = teamDb.type,
            createdAt = teamDb.createdAt?.toLongOrNull(),
            updatedAt = teamDb.updatedAt?.toLongOrNull()
        )
    }

    fun toContact(dbContact: Contact): com.hyperether.goodjob.models.Contact {
        return com.hyperether.goodjob.models.Contact(
            id = dbContact._id,
            name = dbContact.name,
            contactType = dbContact.contactType,
            industry = dbContact.industry,
            addressObject = Mapper().jsonAsObject<AddressObject>(dbContact.address),
            email = dbContact.email,
            phone = dbContact.phone,
            personName = dbContact.personName,
            personEmail = dbContact.personEmail,
            personAddress = dbContact.personAddress,
            personPhone = dbContact.personPhone,
            workspaceId = dbContact.workspaceId,
        )
    }

    fun toPlan(plan: com.hyperether.goodjob.database.Plan): Plan {
        return Plan(
            id = plan.id,
            _id = plan._id,
            name = plan.name,
            price = plan.price,
            perMonth = plan.perMonth,
            description = plan.description,
            storage = plan.storage,
            planSupport = plan.planSupport,
            limitManagers = plan.limitManagers,
            limitUsers = plan.limitUsers,
            currency = plan.currency,
            type = plan.type,
            interval = plan.interval,
            stripePriceId = plan.stripePriceId,
            stripeProductId = plan.stripeProductId,
            isPopular = plan.isPopular == 1L,
            descriptions = plan.descriptions?.let { jsonAsObject<Map<String, String>>(it) }
                ?: emptyMap(),
            names = plan.names?.let { jsonAsObject<Map<String, String>>(it) } ?: emptyMap(),
            storages = plan.storages?.let { jsonAsObject<Map<String, String>>(it) } ?: emptyMap(),
            planSupports = plan.planSupports?.let { jsonAsObject<Map<String, String>>(it) }
                ?: emptyMap()
        )
    }

    inline fun <reified T> listAsJson(list: List<T>?): String? = list?.let {
        Json.encodeToString(ListSerializer(serializer()), it)
    }

    inline fun <reified T> jsonAsList(json: String): List<T> {
        return Json.decodeFromString(ListSerializer(serializer()), json)
    }

    fun listAsJsonSteps(steps: List<Step>?): String {
        return Json.encodeToString(steps)
    }

    fun listAsJsonNotes(list: List<Note>?): String {
        return Json.encodeToString(list)
    }

    fun jsonToListNotes(jsonString: String): List<Note> {
        val noteList: MutableList<Note> = mutableListOf()
        try {
            noteList.addAll(Json.decodeFromString(jsonString))
        } catch (e: Exception) {

        }
        return noteList
    }

    fun toSkill(skills: com.hyperether.goodjob.database.Skills): Skills {
        return Skills(
            id = skills.id,
            _id = skills._id,
            name = skills.name,
            workspaceId = skills.workspaceId
        )
    }

    fun toSkill(skills: Skills): com.hyperether.goodjob.database.Skills {
        return com.hyperether.goodjob.database.Skills(
            _id = skills._id,
            id = skills.id ?: 0,
            name = skills.name,
            workspaceId = skills.workspaceId
        )
    }

    inline fun <reified T> objectAsJson(obj: T?): String? = obj?.let {
        Json.encodeToString(it)
    }

    inline fun <reified T> jsonAsObject(json: String?): T? = json?.let {
        Json.decodeFromString(it)
    }

    fun stepToStepData(step: Step): StepData {
        return StepData(
            title = mutableStateOf(step.stepTitle ?: ""),
            description = mutableStateOf(step.stepDescription ?: ""),
            estimation = mutableStateOf(step.estimatedDuration?.toString() ?: ""),
            stepStatus = mutableStateOf(step.stepStatus ?: Constants.STEP_STATUS_PENDING)
        )
    }

    fun toStepDataList(list: List<Step>): List<StepData> {
        return list.map { step -> stepToStepData(step) }
    }

    fun toJobUpdateStatusRequest(job: Job, status: String): JobRequest {
        val assignees = job.assignees
        val assigneesRequest = AssigneesRequest(
            employees = assignees?.employees?.map { it?._id }?.distinct(),
            teams = assignees?.teams?.map { it?._id }?.distinct(),
        )
        return JobRequest(
            jobTitle = job.jobTitle,
            status = status,
            assignees = assigneesRequest,
            creatorId = job.creatorId,
            workspaceId = job.workspaceId,
            start_at = job.start_at,
            end_at = job.end_at,
            repeat = job.repeat,
            repeatInterval = job.repeatInterval,
            dueDate = job.dueDate,
            startDate = job.startDate,
            jobDuration = job.jobDuration,
            steps = job.steps.orEmpty(),
            userId = job.userId,
            contactSiteId = job.contactSiteId,
            contactSiteName = job.contactSiteName,
            contactParentId = job.contactParentId,
            noteObjects = job.noteObjects
        )
    }

    fun toUserRequest(user: com.hyperether.goodjob.models.User): UserRequest {
        return UserRequest(
            fullName = user.fullName.orEmpty(),
            email = user.email.orEmpty(),
            phone = user.phone.orEmpty(),
            zip = user.zip.orEmpty(),
            country = user.country.orEmpty(),
            city = user.city.orEmpty(),
            address = user.address.orEmpty(),
            status = user.status.orEmpty(),
            workspaceId = user.workspaceId.orEmpty(),
            skills = user.skills.orEmpty(),
            teams = user.teams?.map {
                UserTeam(
                    id = it?.id.orEmpty(),
                    value = it?.value.orEmpty()
                )
            } ?: emptyList(),
            upcomingLeave = user.upcomingLeave,
            role = user.role.orEmpty(),
            workingHours = user.workingHours,
            pendingPlanId = user.pendingPlanId,
            subscriptionEndsAt = user.subscriptionEndsAt,
            pushToken = user.pushToken,
            lang = user.lang
        )
    }

    fun toContactRequest(contact: com.hyperether.goodjob.models.Contact): ContactRequest {
        return ContactRequest(
            name = contact.name,
            contactType = contact.contactType,
            industry = contact.industry,
            addressObject = contact.addressObject,
            email = contact.email,
            phone = contact.phone,
            personName = contact.personName,
            personEmail = contact.personEmail,
            personAddress = contact.personAddress,
            personPhone = contact.personPhone,
            createdAt = contact.createdAt,
            workspaceId = contact.workspaceId,
            updatedAt = contact.updatedAt
        )
    }
}